import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect, useImperativeHandle, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import * as Yup from 'yup';

import { toAbsoluteUrl } from '../../../../_metronic/helpers';
import { BUSINESS_LANGUAGES } from '../../../../common/constants';
import useModal from '../../../../components/modal/useModal';
import { capitalizeFirstLetter } from '../../../../components/utils/Capitilizer';
import { sanitizeFormData } from '../../../../components/utils/deepCleanFormData';
import { isEmpty, scrollOnError } from '../../../../helpers/utils';
import { useGetBusinessQuery } from '../../../../reducers/business/businessApiSlice';
import { selectActiveBusiness } from '../../../../reducers/business/businessSlice';
import { setShowModal } from '../../../../reducers/modalSlice';
import { useCreateStoreMutation, useEditStoreMutation, useGetStoreDetailsQuery } from '../../../../reducers/stores/storeApiSlice';
import { useGetContactInfoTypesQuery } from '../../../../reducers/app/api';
import { DAYS } from '../utils/options';
import details from './details/Details';
import { optionsCurrency, optionsTimezone } from './details/StoreOptions';
import { acceptingBookingOptions, disabledReservationSourcesOptions, managingTablesOptions } from './settingsComponents/settingsOptions';
import TabDetails from './TabDetails';
import TabFiles from './TabFiles';
import TabOpeningHours from './TabOpeningHours';
import TabPayments from './TabPayments';
import TabSettings from './TabSettings';

const StoreModalBody = () => {
	const intl = useIntl();
	const dispatch = useDispatch();
	const { id, ref } = useModal();

	const editMode = !!id;
	const { _id: business_id } = useSelector(selectActiveBusiness);
	const { data: detailsData, isLoading: isLoadingDetails } = useGetStoreDetailsQuery({ bid: business_id, path: { id: id } }, { skip: !editMode });
	const { data: businessData, isLoading: isLoadingBusiness } = useGetBusinessQuery({ path: { id } });
	const { data: contactInfoTypes = [] } = useGetContactInfoTypesQuery();
	const defaultLocale = businessData?.default_locale ?? BUSINESS_LANGUAGES[0].code;

	const rsvProviderTypes = useMemo(() => contactInfoTypes.filter((type) => type.type === 'rsv_provider'), [contactInfoTypes]);
	const rsvProviderTypeIds = useMemo(() => rsvProviderTypes.map((type) => type._id), [rsvProviderTypes]);

	const getStoreValues = (key) => {
		if (detailsData?.item[key].length > 0) {
			return detailsData?.item[key].map((item) => ({
				value: item?._id,
				label: capitalizeFirstLetter(item?.title.toLowerCase()),
			}));
		}
	};

	const getStoreTraitValues = (key) =>
		detailsData?.item['traits']
			.filter((item) => item?.type === key)
			.map((item) => ({
				value: item?._id,
				label: capitalizeFirstLetter(item?.title),
			}));

	const isRsvProviderType = (type) => rsvProviderTypeIds.includes(type);

	const getSocialValues = () =>
		detailsData && editMode
			? detailsData?.item['contact_info']
					.map((item) => ({
						val: item.val,
						type: item?.type,
					}))
					.filter((social) => social.type !== 'phone' && social.type !== 'email' && !isRsvProviderType(social.type))
			: [];

	const getAggregatorValues = () =>
		detailsData && editMode
			? detailsData?.item['contact_info']
					.filter((item) => isRsvProviderType(item.type))
					.map((item) => ({
						val: item.val,
						type: item?.type,
					}))
			: [];

	const getBusinessName = () => {
		const title = businessData?.businesses.some((business) => business.title === details?.item?.business?.title);
		return title ? details?.item?.business?.title : businessData?.businesses ? businessData.businesses[0].title : '';
	};

	const getCurrency = () => `${detailsData?.item?.currency?.code}, ${detailsData?.item?.currency?.name} (${detailsData?.item?.currency?.symbol})`;
	const getReservationValues = () =>
		detailsData && editMode
			? detailsData?.item?.reservation_prefs.map((item) => ({
					value: item?._id,
					label: item?.title,
			  }))
			: [];

	const getDisabledSources = () =>
		detailsData && editMode
			? (detailsData?.item?.online_booking_settings?.disabled_sources ?? []).map((source) => ({
					value: source,
					label: disabledReservationSourcesOptions.find((option) => option.value === source)?.label ?? source,
			  }))
			: [];

	const getTranslations = (schedule) => {
		const keys = [];

		BUSINESS_LANGUAGES?.forEach((i) => {
			keys.push({
				[i.code]: {
					notes: schedule?.translations[i?.code]?.notes ?? '',
				},
			});
		});
		return keys.reduce((acc, item) => {
			const [key, value] = Object.entries(item)[0];
			acc[key] = value;
			return acc;
		}, {});
	};

	const getOpeningHoursValues = () => {
		if (editMode) {
			let schedule = { ...detailsData?.item?.opening_hours };
			Object.keys(schedule).forEach((key) => {
				schedule[key] = {
					day: DAYS[key],
					time: `${schedule[key].start_time} - ${schedule[key].end_time}`,
					_id: uuidv4(),
					...schedule[key],
					translations: schedule[key].translations ? getTranslations(schedule[key]) : { en: '', el: '' },
				};
			});
			return schedule;
		}
		return [];
	};

	const getMenuByLocale = (code) => {
		if (!editMode) {
			return [];
		}

		const rootMenu = Array.isArray(detailsData?.item?.menu) ? detailsData.item.menu : [];
		const translationMenu = Array.isArray(detailsData?.item?.translations?.[code]?.menu) ? detailsData.item.translations[code].menu : [];

		if (rootMenu.some((item) => item?.locale)) {
			return rootMenu.filter((item) => item?.locale === code).map((item) => ({ ...item, locale: item?.locale ?? code }));
		}

		if (code === defaultLocale) {
			const source = rootMenu.length > 0 ? rootMenu : translationMenu;
			return source.map((item) => ({ ...item, locale: item?.locale ?? code }));
		}

		return translationMenu.map((item) => ({ ...item, locale: item?.locale ?? code }));
	};

	const buildTranslations = () => {
		let translations = {};
		BUSINESS_LANGUAGES.forEach((language) => {
			const { code } = language;
			translations[code] = {
				title: editMode
					? code === defaultLocale
						? detailsData?.item?.title ?? ''
						: detailsData?.item?.translations?.[code]?.title ?? ''
					: '',
				description: editMode
					? code === defaultLocale
						? detailsData?.item?.description ?? ''
						: detailsData?.item?.translations?.[code]?.description ?? ''
					: '',
				prepayment_policy: editMode
					? code === defaultLocale
						? detailsData?.item?.prepayment_policy ?? ''
						: detailsData?.item?.translations?.[code]?.prepayment_policy ?? ' '
					: '',
				cancelation_policy: editMode
					? code === defaultLocale
						? detailsData?.item?.cancelation_policy ?? ''
						: detailsData?.item?.translations?.[code]?.cancelation_policy ?? ''
					: '',
				notes: editMode
					? code === defaultLocale
						? detailsData?.item?.notes ?? ''
						: detailsData?.item?.translations?.[code]?.notes ?? ''
					: '',
				menu: editMode ? getMenuByLocale(code) : [],
			};
		});
		return translations;
	};

	const buildOnlineBookingTranslations = () => {
		let translations = {};
		BUSINESS_LANGUAGES.forEach((language) => {
			const { code } = language;
			translations[code] = {
				max_people_notice: editMode ? detailsData?.item?.online_booking_settings?.translations?.[code]?.max_people_notice ?? '' : '',
			};
		});
		return translations;
	};

	const buildLocationTranslations = () => {
		let translations = {};
		BUSINESS_LANGUAGES.forEach((language) => {
			const { code } = language;
			translations[code] = {
				street: editMode
					? code === defaultLocale
						? detailsData?.item?.location?.street ??
						  detailsData?.item?.location?.translations?.[code]?.street ??
						  detailsData?.item?.location?.street_address ??
						  ''
						: detailsData?.item?.location?.translations?.[code]?.street ??
						  detailsData?.item?.location?.translations?.[code]?.street_address ??
						  ''
					: '',
				street_no: editMode
					? code === defaultLocale
						? detailsData?.item?.location?.street_no ?? detailsData?.item?.location?.translations?.[code]?.street_no ?? ''
						: detailsData?.item?.location?.translations?.[code]?.street_no ?? ''
					: '',
				city: editMode ? detailsData?.item?.location?.translations?.[code]?.city ?? '' : '',
				area: editMode ? detailsData?.item?.location?.translations?.[code]?.area ?? '' : '',
				country: editMode ? detailsData?.item?.location?.translations?.[code]?.country ?? 'GR' : 'GR',
			};
		});
		return translations;
	};

	const getLocationLatLng = () => {
		if (!editMode) {
			return { lat: '', lng: '' };
		}
		const loc = detailsData?.item?.location;
		const coords = loc?.coords?.coordinates;
		const lat =
			loc?.lat !== undefined && loc?.lat !== null && loc?.lat !== '' ? loc.lat : Array.isArray(coords) && coords.length >= 2 ? coords[1] : '';
		const lng =
			loc?.lng !== undefined && loc?.lng !== null && loc?.lng !== '' ? loc.lng : Array.isArray(coords) && coords.length >= 2 ? coords[0] : '';
		return { lat, lng };
	};

	const validationSchema = (intl) =>
		Yup.object({
			translations: Yup.object().shape({
				[defaultLocale]: Yup.object().shape({
					title: Yup.string().required(`${intl.formatMessage({ id: 'ERROR_MESSAGE.TITLE_REQUIRED_IN_DEFAULT_LANG' })} (${defaultLocale})`),
					description: Yup.string().required(
						`${intl.formatMessage({ id: 'ERROR_MESSAGE.DESCRIPTION_REQUIRED_IN_DEFAULT_LANG' })} (${defaultLocale})`
					),
				}),
			}),
			email: Yup.string()
				.required(intl.formatMessage({ id: 'ERROR_MESSAGE.EMAIL' }))
				.email(intl.formatMessage({ id: 'ERROR_MESSAGE.EMAIL_NOT_VALID' })),
			location: Yup.object().shape({
				street_address: Yup.string().required(
					`${intl.formatMessage({ id: 'ERROR_MESSAGE.LOCATION_REQUIRED_IN_DEFAULT_LANG' })} (${defaultLocale})`
				),
			}),
			phone: Yup.string().matches(/^\+[1-9]\d{5,14}$/, intl.formatMessage({ id: 'ERROR_MESSAGE.PHONE_FULL_FORMAT' })),
			cuisine: Yup.array()
				.min(1, intl.formatMessage({ id: 'ERROR_MESSAGE.CUISINE' }))
				.required(intl.formatMessage({ id: 'ERROR_MESSAGE.SELECT_CUISINE' })),
			store_type: Yup.array()
				.min(1, intl.formatMessage({ id: 'ERROR_MESSAGE.MIN_CATEGORY' }))
				.required(intl.formatMessage({ id: 'ERROR_MESSAGE.SELECT_CATEGORY' })),
		});

	let storeValues = {};
	useEffect(() => {
		if (!isLoadingDetails && !isLoadingBusiness) {
			const { lat: locationLat, lng: locationLng } = getLocationLatLng();
			storeValues = {
				business: editMode ? getBusinessName() : businessData?.businesses?.find((business) => business?._id === business_id)?.title ?? '',
				businessId: business_id,
				payment_rules_temp: [
					{
						title: '',
						terms: '',
						rule_type: 'per_booking',
						type: 'prepayment',
						amount: 1,
						min_prepayment_guests: 1,
					},
				],
				phone: editMode ? detailsData?.item?.phone ?? '' : '',
				email: editMode ? detailsData?.item?.email ?? '' : '',
				title: editMode ? detailsData?.item?.title ?? '' : '',
				description: editMode ? detailsData?.item?.description ?? '' : '',
				location: {
					street_address: editMode ? detailsData?.item?.location?.street_address ?? '' : '',
					street: editMode
						? detailsData?.item?.location?.street ??
						  detailsData?.item?.location?.translations?.[defaultLocale]?.street ??
						  detailsData?.item?.location?.street_address ??
						  ''
						: '',
					street_no: editMode
						? detailsData?.item?.location?.street_no ?? detailsData?.item?.location?.translations?.[defaultLocale]?.street_no ?? ''
						: '',
					city: editMode ? detailsData?.item?.location?.city ?? '' : '',
					area: editMode ? detailsData?.item?.location?.area ?? '' : '',
					post_code: editMode ? detailsData?.item?.location?.post_code ?? '' : '',
					country: editMode ? detailsData?.item?.location?.country ?? 'GR' : 'GR',
					coords: editMode ? detailsData?.item?.location?.coords ?? {} : {},
					lat: locationLat,
					lng: locationLng,
					translations: buildLocationTranslations(),
				},
				contact_info: {
					social: editMode ? getSocialValues() : [],
					rsv_providers: editMode ? getAggregatorValues() : [],
				},
				currency: {
					code: optionsCurrency.some((c) => c.label === getCurrency())
						? getCurrency()
						: optionsCurrency[0].label ?? optionsCurrency[0].label,
					symbol: '€',
					name: 'Euro',
				},
				timezone: detailsData?.item?.timezone ?? optionsTimezone[0].value,
				store_type: getStoreValues('store_type'),
				default_store_type: detailsData?.item?.default_store_type ?? '',
				cuisine: getStoreValues('cuisine') ?? [],
				default_cuisine: getStoreValues('cuisine') ?? '',
				traits:
					{
						tags: getStoreTraitValues('tags') ?? [],
						paymentMethods: getStoreTraitValues('payment_methods') ?? [],
						suggestions: getStoreTraitValues('suggested_for') ?? [],
						environment: getStoreTraitValues('environment') ?? [],
						services: getStoreTraitValues('services') ?? [],
						misc:
							editMode && getStoreTraitValues('misc')
								? getStoreTraitValues('misc').map((item) => ({
										...item,
										checked: true,
								  })) ?? []
								: [],
						info:
							editMode && getStoreTraitValues('misc')
								? getStoreTraitValues('info').map((item) => ({
										...item,
										checked: true,
								  })) ?? []
								: [],
					} ?? [],
				reservation_prefs: getReservationValues() ?? [],
				prepayment: detailsData?.item?.prepayment ?? '',
				prepayment_policy: detailsData?.item?.prepayment_policy ?? '',
				cancelation_policy: detailsData?.item?.cancelation_policy ?? '',
				cancel_max_time_hrs: detailsData?.item?.cancel_max_time_hrs ?? 0,
				notes: detailsData?.item?.notes ?? '',
				published: detailsData?.item?.published ?? true,
				opening_hours: editMode ? getOpeningHoursValues() ?? {} : {},
				opening_hours_temp: {},
				payment_rules: detailsData?.item?.payment_rules ?? [],
				online_booking_settings: {
					assign_tables: detailsData?.item?.online_booking_settings?.assign_tables ?? managingTablesOptions[0].value,
					accept_bookings: detailsData?.item?.online_booking_settings?.accept_bookings ?? acceptingBookingOptions[0].value,
					disabled: detailsData?.item?.online_booking_settings?.disabled ?? false,
					disabled_sources: getDisabledSources(),
					accept_waitlists: detailsData?.item?.online_booking_settings?.accept_waitlists ?? false,
					min_people: detailsData?.item?.online_booking_settings?.min_people ?? 1,
					max_people: detailsData?.item?.online_booking_settings?.max_people ?? 15,
					max_people_notice: detailsData?.item?.online_booking_settings?.max_people_notice ?? '',
					time_interval_mins: detailsData?.item?.online_booking_settings?.time_interval_mins ?? 30,
					default_duration_mins: detailsData?.item?.online_booking_settings?.default_duration_mins ?? 120,
					min_advance_time_mins: detailsData?.item?.online_booking_settings?.min_advance_time_mins ?? 0,
					max_advance_time_days: detailsData?.item?.online_booking_settings?.max_advance_time_days ?? 180,
					max_similar: detailsData?.item?.online_booking_settings?.max_similar ?? 0,
					require_prepayment: detailsData?.item?.online_booking_settings?.require_prepayment ?? false,
					refund_pct: detailsData?.item?.online_booking_settings?.refund_pct ?? 0,
					prepayment_pm_rule:
						detailsData?.item?.online_booking_settings?.prepayment_pm_rule ??
						detailsData?.item?.payment_rules.find((i) => i.type === 'prepayment')?._id ??
						'',
					charge_no_show_fee: detailsData?.item?.online_booking_settings?.charge_no_show_fee ?? false,
					no_show_pm_rule:
						detailsData?.item?.online_booking_settings?.no_show_pm_rule ??
						detailsData?.item?.payment_rules.find((i) => i.type === 'no_show_fee')?._id ??
						'',
					vat_pct: detailsData?.item?.online_booking_settings?.vat_pct ?? 0,
					use_rsv_providers: detailsData?.item?.online_booking_settings?.use_rsv_providers ?? false,
					translations: buildOnlineBookingTranslations(),
				},
				banner: detailsData?.item?.banner ?? [],
				menu: Array.isArray(detailsData?.item?.menu)
					? detailsData.item.menu.map((item) => ({ ...item, locale: item?.locale ?? defaultLocale }))
					: [],
				media: detailsData?.item?.media ?? [],
				price_rating: detailsData?.item?.price_rating ?? '1',
				use_business_social: detailsData?.item?.use_business_social ?? setValue('use_business_social', false),
				translations: buildTranslations(),
			};
		}
	}, [isLoadingDetails, isLoadingBusiness, rsvProviderTypeIds]);

	const {
		handleSubmit,
		register,
		watch,
		reset,
		resetField,
		control,
		trigger,
		setValue,
		setError,
		clearErrors,
		formState: { errors, isSubmitting, dirtyFields },
	} = useForm({
		mode: 'onSubmit',
		defaultValues: { ...storeValues },
		shouldFocusError: true,
		resolver: yupResolver(validationSchema(intl)),
	});

	/** Watch change of each entry **/
	// console.log(watch({ ...storeValues }));
	/** Show errors captured by validation **/
	// console.log(errors ? errors : 'Store information submitted successfully');

	useEffect(() => {
		if (!isLoadingDetails && !isLoadingBusiness) {
			reset({ ...storeValues });
		}
	}, [detailsData, isLoadingBusiness, isLoadingDetails, reset, rsvProviderTypeIds]);

	const [createStore] = useCreateStoreMutation();
	const [updateStore] = useEditStoreMutation();

	useImperativeHandle(ref, () => ({ onSubmit: () => handleSubmit(onSubmit, scrollOnError)(), isSubmitting: isSubmitting }));

	const closeModal = () => {
		dispatch(setShowModal({ type: 'location', show: false }));
	};

	const onSubmit = async (data) => {
		const cleanedData = sanitizeFormData(data, {
			preserveEmptyArrays: false,
			preserveEmptyObjects: false,
			preserveDirtyValues: true,
			dirtyValues: dirtyFields,
		});
		const result = { ...cleanedData };
		const businessId = result.businessId ?? business_id;
		const traits = [];
		const cuisine = [];
		const storeType = [];
		const info = [];
		const reservation = [];
		/** Prepare Object for submission (Transform state and remove keys) **/

		/** Obsolete keys **/
		// Business
		if (result.business) {
			delete result.business;
		}
		if (result.payment_rules_temp) {
			delete result.payment_rules_temp;
		}
		if (result.opening_hours_temp) {
			delete result.opening_hours_temp;
		}
		/** Transformed Object **/
		// Traits
		if (result.traits && !isEmpty(result.traits)) {
			for (const key in result.traits) {
				if (Object.hasOwn(result.traits, key)) {
					result.traits[key].forEach((item) => {
						if (key === 'info' || key === 'misc') {
							if (item.checked === true) {
								traits.push(item.value);
							}
							delete result.traits[key].find((i) => i.value === item.value);
						} else traits.push(item.value);
					});
				}
			}
		}

		// Store Type
		if (result.store_type && !isEmpty(result.store_type)) {
			result.store_type.forEach((item) => storeType.push(item.value));
		}

		// Contact Information
		if (result.contact_info && !isEmpty(result.contact_info)) {
			const details = getSocialValues();
			for (const key in result.contact_info) {
				result.contact_info[key].forEach((item) => {
					if (editMode && !result.contact_info[key].includes(item)) {
						info.push(item);
					}
					info.push(item);
				});
			}
		}

		// Reservation preferences
		if (result.reservation_prefs && !isEmpty(result.reservation_prefs)) {
			result.reservation_prefs.forEach((item) => reservation.push(item.value));
		}

		// Opening hours
		if (result.opening_hours && !isEmpty(result.opening_hours)) {
			for (const [key] of Object.entries(result.opening_hours)) {
				result.opening_hours[key].max_bookings =
					result.opening_hours[key].max_bookings === 'Unlimited' ? 0 : parseInt(result.opening_hours[key].max_bookings);
				result.opening_hours[key].max_guests =
					result.opening_hours[key].max_guests === 'Unlimited' ? 0 : parseInt(result.opening_hours[key].max_guests);
				result.opening_hours[key].max_interval_bookings =
					result.opening_hours[key].max_interval_bookings === 'Unlimited' ? 0 : parseInt(result.opening_hours[key].max_interval_bookings);
				result.opening_hours[key].max_interval_guests =
					result.opening_hours[key].max_interval_guests === 'Unlimited' ? 0 : parseInt(result.opening_hours[key].max_interval_guests);
				if (
					result.opening_hours[key]?.translations?.[defaultLocale]?.notes !== '' &&
					result.opening_hours[key]?.notes !== result.opening_hours[key]?.translations?.[defaultLocale]?.notes
				) {
					result.opening_hours[key].notes = result.opening_hours[key]?.translations?.[defaultLocale]?.notes;
				}
				delete result.opening_hours[key].day;
				delete result.opening_hours[key].time;
				delete result.opening_hours[key]._id;
			}
		}

		// Payment Rules
		if (result.payment_rules && !isEmpty(result.payment_rules)) {
			result.payment_rules.forEach((i, index) => {
				if (i.min_prepayment_guests === 'Always (1)') {
					result.payment_rules[index].min_prepayment_guests = 1;
				} else {
					result.payment_rules[index].min_prepayment_guests = i.min_prepayment_guests;
				}

				i.rule_type = 'per_booking';
				i.charge_type = 'pre_payment';
				i.title = i.translations?.[defaultLocale]?.title ?? i?.title;
				i.terms = i.translations?.[defaultLocale]?.terms ?? i?.title;

				// detailsData?.payment_rules && detailsData?.payment_rules.length > 0
				// 	? detailsData.find((item) => item._id !== i._id)
				// 		? delete i._id
				// 		: delete result.payment_rules[index]._id
				// 	: delete i._id;
			});
			delete result.payment_rules_temp;
		} else result.payment_rules = [];

		if (result.store_type && !isEmpty(result.store_type)) {
			// Default
			if (result.default_store_type === '') {
				result.default_store_type = result.store_type[0]._id;
			}
			delete result.default_store_type;
		} else delete result.default_store_type;

		// Cuisine
		if (result.cuisine && !isEmpty(result.cuisine)) {
			// Default
			if (result.default_cuisine === '') {
				result.default_cuisine = result.cuisine[0]._id;
			}
			result.cuisine.forEach((item) => cuisine.push(item.value));
		} else delete result.default_cuisine;

		// Online booking settings
		if (Object.keys(result.online_booking_settings).length >= 1) {
			result.online_booking_settings.max_people = parseInt(result.online_booking_settings.max_people ?? 15);
			result.online_booking_settings.min_people = parseInt(result.online_booking_settings.min_people ?? 1);
			result.online_booking_settings.max_advance_time_days = parseInt(result.online_booking_settings.max_advance_time_days ?? 1);
			result.online_booking_settings.min_advance_time_mins = parseInt(result.online_booking_settings.min_advance_time_mins ?? 1);
			result.online_booking_settings.time_interval_mins = parseInt(result.online_booking_settings.time_interval_mins);
			result.online_booking_settings.default_duration_mins = parseInt(result.online_booking_settings.default_duration_mins) ?? 120;
			result.online_booking_settings.max_similar = parseInt(result.online_booking_settings.max_similar) ?? 10;
			result.online_booking_settings.refund_pct = parseInt(result.online_booking_settings.refund_pct ?? 0);
			result.online_booking_settings.vat_pct = parseInt(result.online_booking_settings.vat_pct ?? 0);
			result.online_booking_settings.max_people_notice = result.online_booking_settings.max_people_notice ?? '';
			result.online_booking_settings.disabled_sources =
				result.online_booking_settings?.disabled_sources?.map((source) => source?.value ?? source) ?? [];
		} else delete result.online_booking_settings;
		result.traits = traits;
		result.cuisine = cuisine;
		result.contact_info = info;
		result.reservation_prefs = reservation;
		result.store_type = storeType;
		result.published = result.published ?? true;
		result.currency = { code: 'EUR', symbol: '€', name: 'Euro' };
		result.online_booking_settings.assign_tables = result.online_booking_settings.assign_tables ?? 'auto';
		result.online_booking_settings.accept_bookings = result.online_booking_settings.accept_bookings ?? 'manual';

		// Default translations to parent keys
		result.title = result?.translations?.[defaultLocale]?.title ?? result?.title ?? '';
		result.description = result?.translations?.[defaultLocale]?.description ?? result?.description ?? '';
		result.prepayment_policy = result?.translations?.[defaultLocale]?.prepayment_policy;
		result.cancelation_policy = result?.translations?.[defaultLocale]?.cancelation_policy;
		result.notes = result?.translations?.[defaultLocale]?.notes;
		const menuByLocale = new Map();
		BUSINESS_LANGUAGES.forEach(({ code }) => {
			const files = result?.translations?.[code]?.menu;

			if (files && files.length > 0) {
				menuByLocale.set(
					code,
					files.map((file) => ({ ...file, locale: file?.locale ?? code }))
				);
			}
		});
		result.menu = Array.from(menuByLocale.values()).flat();
		result.online_booking_settings.title = result.online_booking_settings?.translations?.[defaultLocale]?.title;
		result.online_booking_settings.terms = result.online_booking_settings?.translations?.[defaultLocale]?.terms;
		// Non-translatable coordinates on location: sync GeoJSON coords from lat/lng
		const { lat, lng } = result.location ?? {};
		if (lat !== undefined && lat !== null && lat !== '' && lng !== undefined && lng !== null && lng !== '') {
			const latNum = typeof lat === 'number' ? lat : parseFloat(String(lat).replace(',', '.'));
			const lngNum = typeof lng === 'number' ? lng : parseFloat(String(lng).replace(',', '.'));
			if (!Number.isNaN(latNum) && !Number.isNaN(lngNum)) {
				result.location.coords = { type: 'Point', coordinates: [lngNum, latNum] };
			}
		}
		// result.location.street_address = result.location.translations?.[defaultLocale]?.street_address ?? result.location.street_address ?? '';
		// result.location.city = result.location.translations?.[defaultLocale]?.city ?? result.location.city ?? '';
		// result.location.area = result.location.translations?.[defaultLocale]?.area ?  result.location.area ?? '';
		// result.location.country = result.location.translations?.[defaultLocale]?.country ?? result.location.country ?? 'GR';

		// Submission
		if (Object.keys(errors).length === 0) {
			editMode
				? await updateStore({ bid: businessId ?? business_id, path: { id: id }, body: { ...result } })
				: await createStore({ bid: businessId ?? business_id, path: { id: id }, body: { ...result } });
			delete result.businessId;
			closeModal();
		}
	};
	const loader = () => (
		<div className="d-flex justify-content-center">
			<div className="spinner-border" role="status">
				<span className="sr-only">
					<FormattedMessage id="GENERAL.LOADING"></FormattedMessage>
				</span>
			</div>
		</div>
	);

	return (
		<>
			<ul className="nav nav-tabs nav-line-tabs d-flex justify-content-sm-center flex-nowrap overflow-auto mb-5 fs-3">
				<li className="nav-item">
					<a className="nav-link text-nowrap active" data-bs-toggle="tab" href="#kt_tab_details" name="details">
						{Object.keys(errors).length > 0 && !errors?.payment_rules ? (
							<img alt="error-icon" className="mx-2" src={toAbsoluteUrl('/icons/error.svg')} width={15} />
						) : null}
						<FormattedMessage id="STORE.MODAL.DETAILS" />
					</a>
				</li>
				<li className="nav-item">
					<a className="nav-link text-nowrap" data-bs-toggle="tab" href="#kt_tab_opening_hours" name="opening_hours">
						<FormattedMessage id="STORE.MODAL.OPENING_HOURS" />
					</a>
				</li>
				<li className="nav-item">
					<a className="nav-link text-nowrap" data-bs-toggle="tab" href="#kt_tab_settings" name="settings">
						<FormattedMessage id="STORE.MODAL.SETTINGS" />
					</a>
				</li>
				<li className="nav-item">
					<a className="nav-link text-nowrap" data-bs-toggle="tab" href="#kt_tab_payments" name="payments">
						{errors?.payment_rules ? <img alt="error-icon" className="mx-2" src={toAbsoluteUrl('/icons/error.svg')} width={15} /> : null}
						<FormattedMessage id="STORE.MODAL.PAYMENTS" />
					</a>
				</li>
				<li className="nav-item">
					<a className="nav-link text-nowrap" data-bs-toggle="tab" href="#kt_tab_files" name="files">
						<FormattedMessage id="STORE.MODAL.FILES" />
					</a>
				</li>
			</ul>

			<div className="tab-content" id="myTabContent">
				<div className="tab-pane fade active show" id="kt_tab_details" role="tabpanel">
					{!isLoadingDetails ? (
						<TabDetails
							register={register}
							control={control}
							watch={watch}
							errors={errors}
							trigger={trigger}
							setValue={setValue}
							resetField={resetField}
							reset={reset}
							defaultLocale={defaultLocale}
						/>
					) : (
						loader()
					)}
				</div>
				<div className="tab-pane fade" id="kt_tab_opening_hours" role="tabpanel">
					{!isLoadingDetails ? (
						<TabOpeningHours
							register={register}
							resetField={resetField}
							control={control}
							watch={watch}
							trigger={trigger}
							errors={errors}
							setValue={setValue}
							defaultLocale={defaultLocale}
						/>
					) : (
						loader()
					)}
				</div>
				<div className="tab-pane fade" id="kt_tab_settings" role="tabpanel">
					{!isLoadingDetails ? (
						<TabSettings register={register} control={control} watch={watch} errors={errors} trigger={trigger} setValue={setValue} />
					) : (
						loader()
					)}
				</div>
				<div className="tab-pane fade" id="kt_tab_payments" role="tabpanel">
					{!isLoadingDetails ? (
						<TabPayments
							register={register}
							control={control}
							watch={watch}
							errors={errors}
							trigger={trigger}
							reset={reset}
							setValue={setValue}
							defaultLocale={defaultLocale}
							setError={setError}
							clearErrors={clearErrors}
						/>
					) : (
						loader()
					)}
				</div>
				<div className="tab-pane fade" id="kt_tab_files" role="tabpanel">
					{' '}
					{!isLoadingDetails ? (
						<TabFiles
							register={register}
							control={control}
							watch={watch}
							errors={errors}
							setValue={setValue}
							defaultLocale={defaultLocale}
						/>
					) : (
						loader()
					)}
				</div>
			</div>
		</>
	);
};

export default StoreModalBody;
